import { Helmet } from "react-helmet-async";

import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const BUSINESS = {
  legalName: "Sagar Lifestyle NY LLC",
  displayName: "Sagar Lifestyle NY",
  registeredAddress: "7912 5th Avenue, Bay Ridge, Brooklyn, NY 11209",
  returnAddress: "7912 5th Avenue, Bay Ridge, Brooklyn, NY 11209",
  supportEmail: "sagarfashion777@gmail.com",
  businessEmail: "sagarfashion777@gmail.com",
  phone: "+1 (718) 630-1100",
  supportHours: "Mon–Fri 11:00 AM–6:00 PM, Sat–Sun 11:00 AM–7:00 PM (ET)",
  governingState: "New York",
};

const DATES = {
  shippingPolicyUpdated: "[EFFECTIVE DATE]",
  termsUpdated: "[EFFECTIVE DATE]",
  privacyUpdated: "[EFFECTIVE DATE]",
};

const SHIPPING = {
  defaultFlatCharge: 9.0,
  freeShippingThreshold: 100.0,
  processingTime: "[ORDER PROCESSING TIME]",
  returnWindow: "[RETURN WINDOW]",
  refundProcessingTime: "[REFUND PROCESSING TIME]",
  cancellationRule: "[CANCELLATION RULE]",
  deliveryCountries: "[DELIVERY COUNTRIES]",
  internationalShippingSupported: false,
  exampleWeightTiers: [
    { range: "0 – 500 g", charge: "$4.99" },
    { range: "501 g – 1 kg", charge: "$7.99" },
    { range: "1 kg – 2 kg", charge: "$11.99" },
    { range: "2 kg – 5 kg", charge: "$18.99" },
    { range: "Above 5 kg", charge: "$2.50 per additional kg" },
  ],
};

const PROVIDERS = {
  paymentProcessor: "Stripe",
  shippingPartners: "[SHIPPING PROVIDER]",
  analyticsProvider: "[ANALYTICS PROVIDER]",
  emailServiceProvider: "[EMAIL SERVICE PROVIDER]",
  smsWhatsappEnabled: false,
};

const SOCIAL = {
  instagram: "[INSTAGRAM URL]",
  facebook: "[FACEBOOK URL]",
  whatsapp: "[WHATSAPP URL]",
};

const POLICY_NAV = [
  { label: "Shipping Policy", path: "/shipping-policy" },
  { label: "Terms & Conditions", path: "/terms" },
  { label: "Privacy Policy", path: "/privacy" },
];

function PolicyNavigation() {
  const { pathname } = useLocation();
  return (
    <nav aria-label="Policy pages" className="border-b border-stone-100 bg-white sticky top-0 z-20">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex gap-1 overflow-x-auto no-scrollbar py-3 -mx-1">
          {POLICY_NAV.map(item => {
            const active = pathname === item.path;
            return (
              <Link key={item.path} to={item.path} aria-current={active ? "page" : undefined}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${active ? "bg-stone-900 text-white" : "text-stone-500 hover:bg-stone-100 hover:text-stone-900"}`}>
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

function LastUpdated({ date }) {
  return <p className="text-xs text-stone-400 font-medium">Last updated: <span className={date?.startsWith("[") ? "text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" : "text-stone-600"}>{date}</span></p>;
}

function PolicyHero({ eyebrow, title, subtitle, updated }) {
  return (
    <header className="max-w-3xl mx-auto px-4 pt-12 pb-6 text-center">
      <p className="text-xs font-bold uppercase tracking-widest text-rose-500 mb-3">{eyebrow}</p>
      <h1 className="serif text-3xl md:text-4xl font-bold text-stone-900 mb-4">{title}</h1>
      <p className="text-stone-500 text-base leading-relaxed">{subtitle}</p>
      <div className="mt-5 flex justify-center"><LastUpdated date={updated} /></div>
    </header>
  );
}

function QuickSummary({ points }) {
  return <div className="max-w-3xl mx-auto px-4 mb-10"><div className="bg-stone-50 border border-stone-200 rounded-3xl p-6"><p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">In short</p><ul className="space-y-2">{points.map((p,i)=><li key={i} className="flex items-start gap-2.5 text-sm text-stone-700 leading-relaxed"><svg className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg><span>{p}</span></li>)}</ul></div></div>;
}

function PolicyTableOfContents({ sections, activeId }) {
  return <><div className="lg:hidden max-w-3xl mx-auto px-4 mb-8"><div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">{sections.map(s=><a key={s.id} href={`#${s.id}`} className={`flex-shrink-0 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${activeId===s.id ? "bg-stone-900 text-white border-stone-900" : "text-stone-500 border-stone-200 hover:border-stone-400"}`}>{s.title}</a>)}</div></div><nav aria-label="Table of contents" className="hidden lg:block"><div className="sticky top-24 w-56"><p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3 px-2">On this page</p><ul className="space-y-0.5">{sections.map(s=><li key={s.id}><a href={`#${s.id}`} className={`block px-3 py-1.5 rounded-lg text-sm transition-colors ${activeId===s.id ? "bg-stone-100 text-stone-900 font-semibold" : "text-stone-500 hover:text-stone-900 hover:bg-stone-50"}`}>{s.title}</a></li>)}</ul></div></nav></>;
}

function useActiveSection(sectionIds) {
  const [activeId,setActiveId]=useState(sectionIds[0]);
  useEffect(()=>{const observer=new IntersectionObserver(entries=>entries.forEach(entry=>{if(entry.isIntersecting)setActiveId(entry.target.id)}),{rootMargin:"-15% 0px -70% 0px"});sectionIds.forEach(id=>{const el=document.getElementById(id);if(el)observer.observe(el)});return()=>observer.disconnect()},[sectionIds]);
  return activeId;
}

function PolicySection({ id, number, title, children }) {
  return <section id={id} className="mb-12 scroll-mt-24"><div className="flex items-baseline gap-3 mb-4">{number&&<span className="serif text-lg text-rose-400 font-bold">{number}</span>}<h2 className="serif text-xl md:text-2xl font-bold text-stone-900">{title}</h2></div><div className="prose-policy text-stone-600 text-sm md:text-[15px] leading-relaxed space-y-3">{children}</div></section>;
}

function InfoCard({ icon, title, children, tone="neutral" }) {
  const tones={neutral:"bg-stone-50 border-stone-200",amber:"bg-amber-50 border-amber-200",rose:"bg-rose-50 border-rose-200"};
  return <div className={`rounded-2xl border p-5 my-4 ${tones[tone]}`}><div className="flex items-start gap-3">{icon&&<span className="text-xl leading-none">{icon}</span>}<div className="flex-1">{title&&<p className="font-semibold text-stone-900 text-sm mb-1">{title}</p>}<div className="text-sm text-stone-600 leading-relaxed">{children}</div></div></div></div>;
}

function TrustCard({ icon, title, children }) {
  return <div className="bg-white rounded-2xl border border-stone-100 p-5 text-center"><div className="w-11 h-11 rounded-xl bg-stone-50 flex items-center justify-center text-xl mx-auto mb-3">{icon}</div><p className="font-semibold text-stone-900 text-sm mb-1.5">{title}</p><p className="text-xs text-stone-500 leading-relaxed">{children}</p></div>;
}

function TrustStrip() {
  return <div className="max-w-5xl mx-auto px-4 py-12"><div className="text-center mb-8"><h2 className="serif text-2xl font-bold text-stone-900 mb-2">How we look after your order</h2><p className="text-stone-500 text-sm">The same four commitments apply across every policy on this site.</p></div><div className="grid grid-cols-2 md:grid-cols-4 gap-4"><TrustCard icon="🔒" title="Secure Shopping">We use reasonable safeguards to protect the information used to run your account and orders.</TrustCard><TrustCard icon="📦" title="Order Visibility">Track your order from shipment through delivery whenever tracking is available.</TrustCard><TrustCard icon="🤝" title="Customer Support">If something goes wrong, contact us and we'll investigate and help.</TrustCard><TrustCard icon="💳" title="Transparent Charges">Shipping charges and order totals are shown before you complete payment.</TrustCard></div></div>;
}

function PolicyTable({ columns, rows }) {
  return <div className="my-4"><div className="hidden sm:block overflow-x-auto rounded-2xl border border-stone-200"><table className="w-full text-sm"><thead><tr className="bg-stone-50 border-b border-stone-200">{columns.map((c,i)=><th key={i} className="text-left px-4 py-3 font-semibold text-stone-500 uppercase text-xs tracking-wide">{c}</th>)}</tr></thead><tbody>{rows.map((row,i)=><tr key={i} className="border-b border-stone-100 last:border-0">{row.map((cell,j)=><td key={j} className="px-4 py-3 text-stone-700">{cell}</td>)}</tr>)}</tbody></table></div><div className="sm:hidden space-y-3">{rows.map((row,i)=><div key={i} className="rounded-2xl border border-stone-200 p-4 bg-white">{row.map((cell,j)=><div key={j} className="flex justify-between py-1 text-sm"><span className="text-stone-400">{columns[j]}</span><span className="text-stone-800 font-medium text-right">{cell}</span></div>)}</div>)}</div></div>;
}

function PolicyFAQ({ title="Frequently asked questions", items }) {
  const [openIndex,setOpenIndex]=useState(null);
  return <div className="max-w-3xl mx-auto px-4 py-4"><h2 className="serif text-2xl font-bold text-stone-900 mb-6">{title}</h2><div className="space-y-2">{items.map((item,i)=>{const open=openIndex===i;return <div key={i} className="border border-stone-200 rounded-2xl overflow-hidden"><button onClick={()=>setOpenIndex(open?null:i)} aria-expanded={open} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-stone-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400"><span className="font-medium text-stone-900 text-sm">{item.q}</span><svg className={`w-4 h-4 text-stone-400 flex-shrink-0 transition-transform duration-200 ${open?"rotate-180":""}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg></button>{open&&<div className="px-5 pb-4 text-sm text-stone-600 leading-relaxed">{item.a}</div>}</div>})}</div></div>;
}

function SocialLinks() {
  const links=[{label:"Instagram",value:SOCIAL.instagram},{label:"Facebook",value:SOCIAL.facebook},{label:"WhatsApp",value:SOCIAL.whatsapp}];
  return <div className="mt-5 flex flex-wrap justify-center gap-2">{links.map(link=>link.value?.startsWith("[")?<span key={link.label} className="px-4 py-2 rounded-full bg-white/5 text-amber-400 text-xs font-semibold">{link.label}: {link.value}</span>:<a key={link.label} href={link.value} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-white/10 text-white text-xs font-semibold hover:bg-white/20 transition-colors">{link.label}</a>)}</div>;
}

function PolicyContact() {
  const rows=[{label:"Support email",value:BUSINESS.supportEmail},{label:"Business email",value:BUSINESS.businessEmail},{label:"Phone",value:BUSINESS.phone},{label:"Business address",value:BUSINESS.registeredAddress},{label:"Support hours",value:BUSINESS.supportHours}];
  return <div className="max-w-3xl mx-auto px-4 py-12"><div className="bg-stone-900 rounded-3xl p-8 md:p-10 text-center"><h2 className="serif text-2xl font-bold text-white mb-2">Questions about this policy?</h2><p className="text-stone-300 text-sm mb-6">We're here to help — reach out any time.</p><div className="grid sm:grid-cols-2 gap-3 text-left max-w-md mx-auto">{rows.map(r=><div key={r.label} className="bg-white/5 rounded-xl px-4 py-3"><p className="text-[11px] text-stone-400 uppercase tracking-wide mb-0.5">{r.label}</p><p className={`text-sm font-medium ${r.value?.startsWith("[")?"text-amber-400":"text-white"}`}>{r.value}</p></div>)}</div><SocialLinks /></div></div>;
}

function RelatedPolicies({ exclude }) {
  return <div className="max-w-3xl mx-auto px-4 pb-16"><p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">Related policies</p><div className="flex flex-wrap gap-2">{POLICY_NAV.filter(p=>p.label!==exclude).map(p=><Link key={p.path} to={p.path} className="px-4 py-2 rounded-full border border-stone-200 text-sm font-medium text-stone-600 hover:border-rose-300 hover:text-rose-500 transition-colors">{p.label} →</Link>)}</div></div>;
}

function BackToTop() {
  const [visible,setVisible]=useState(false);
  useEffect(()=>{const onScroll=()=>setVisible(window.scrollY>600);window.addEventListener("scroll",onScroll);return()=>window.removeEventListener("scroll",onScroll)},[]);
  if(!visible)return null;
  return <button onClick={()=>window.scrollTo({top:0,behavior:"smooth"})} aria-label="Back to top" className="fixed bottom-6 right-6 w-11 h-11 rounded-full bg-stone-900 text-white shadow-lg flex items-center justify-center hover:bg-rose-500 transition-colors z-30"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg></button>;
}

function Placeholder({ value }) {
  const isUnfilled=typeof value==="string"&&value.startsWith("[");
  if(!isUnfilled)return <>{value}</>;
  return <span className="inline-block px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-xs font-semibold align-middle">{value}</span>;
}

function PolicyLayout({ children }) {
  return <div className="min-h-screen bg-white"><style>{`.no-scrollbar{scrollbar-width:none;-ms-overflow-style:none}.no-scrollbar::-webkit-scrollbar{display:none}.prose-policy ul,.prose-policy ol{margin-top:.25rem}.prose-policy a{text-underline-offset:2px}@media(prefers-reduced-motion:reduce){*,*::before,*::after{transition-duration:.01ms!important;animation-duration:.01ms!important}}`}</style><PolicyNavigation />{children}<BackToTop /></div>;
}

const SECTIONS = [
  { id: "intro", title: "Introduction & Acceptance" },
  { id: "eligibility", title: "Eligibility & Accounts" },
  { id: "orders", title: "Orders, Pricing & Payment" },
  { id: "products", title: "Product Information" },
  { id: "shipping-delivery", title: "Shipping & Delivery" },
  { id: "cancel-return-refund", title: "Cancellations, Returns & Refunds" },
  { id: "promotions", title: "Promotions & Gift Cards" },
  { id: "reviews", title: "Reviews & Content" },
  { id: "ip", title: "Intellectual Property" },
  { id: "conduct", title: "Prohibited Activities & Fraud Prevention" },
  { id: "third-party", title: "Third-Party Services & Links" },
  { id: "liability", title: "Liability, Force Majeure & Disputes" },
  { id: "general", title: "General Terms" },
  { id: "faq", title: "FAQ" },
];

export default function TermsAndConditions() {
  const activeId = useActiveSection(SECTIONS.map(s => s.id));

  return (
    <PolicyLayout>
      <Helmet>
        <title>Terms & Conditions | {BUSINESS.legalName}</title>
        <meta name="description" content="The rules and responsibilities for shopping with Sagar Lifestyle NY LLC." />
        <link rel="canonical" href="[CANONICAL URL]" />
        <meta property="og:title" content={`Terms & Conditions | ${BUSINESS.legalName}`} />
        <meta property="og:description" content="Plain-English terms covering orders, payment, shipping, returns, and your responsibilities as a customer." />
      </Helmet>

      <PolicyHero
        eyebrow="Terms"
        title="Terms & Conditions"
        subtitle="The rules and responsibilities that help keep shopping with Sagar Lifestyle NY LLC safe, fair, and transparent."
        updated={DATES.termsUpdated}
      />

      <QuickSummary
        points={[
          "By placing an order or using this site, you agree to these terms.",
          "Orders, prices, and availability can change — we'll always show current details before you pay.",
          "Returns, refunds, and cancellations follow the windows and steps set out below.",
          "We may take reasonable steps to prevent fraud and protect customers and the business.",
        ]}
      />

      <div className="max-w-5xl mx-auto px-4 grid lg:grid-cols-[224px_1fr] gap-10">
        <PolicyTableOfContents sections={SECTIONS} activeId={activeId} />

        <div className="max-w-2xl">
          <PolicySection id="intro" number="1" title="Introduction & Acceptance of Terms">
            <p>
              These Terms & Conditions ("Terms") govern your use of the {BUSINESS.legalName} website
              and your purchase of products from us. By placing an order, creating an account, or
              otherwise using this site, you agree to these Terms. If you don't agree, please don't
              use the site or place an order.
            </p>
            <p>
              {BUSINESS.legalName} is a retailer of lifestyle and fashion products, registered at{" "}
              <Placeholder value={BUSINESS.registeredAddress} />.
            </p>
          </PolicySection>

          <PolicySection id="eligibility" number="2" title="Eligibility, Accounts & Customer Information">
            <p>
              You must be able to form a legally binding contract to place an order — generally, this
              means being an adult under the law of your jurisdiction, or shopping with the involvement
              of a parent or guardian where required.
            </p>
            <p>When you create an account, you agree to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide accurate, current information, including your shipping address and contact details.</li>
              <li>Keep your login credentials confidential.</li>
              <li>Notify us promptly of any unauthorized use of your account.</li>
            </ul>
            <p>We may suspend or terminate accounts that violate these Terms, engage in fraud, or pose a security risk, in line with applicable law.</p>
          </PolicySection>

          <PolicySection id="orders" number="3" title="Orders, Product Availability, Pricing, Taxes & Payment">
            <p>Placing an order is straightforward, but a few things are worth understanding:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Availability:</strong> All orders are subject to product availability. Occasionally an item may sell out after you order — if that happens, we'll contact you.</li>
              <li><strong>Pricing:</strong> Prices are shown at checkout and can change over time. Applicable taxes are calculated and displayed before you complete payment.</li>
              <li><strong>Pricing errors:</strong> If a pricing or listing error occurs, we may correct it and will let you know before proceeding, rather than silently charging a different amount.</li>
              <li><strong>Payment:</strong> Payment must be successfully authorized before an order is confirmed. Payments are processed through {PROVIDERS.paymentProcessor}.</li>
              <li><strong>Order confirmation:</strong> Receiving an order confirmation doesn't guarantee the order can't later be cancelled — for example, where fraud checks, pricing errors, or stock issues arise, we may cancel and refund the order.</li>
              <li><strong>Fraud prevention:</strong> We may run reasonable checks to detect and prevent suspicious or fraudulent transactions before or after an order is placed.</li>
            </ul>
            <InfoCard icon="⚠️" tone="amber">
              We aim to fulfill every order placed in good faith, but we may cancel orders in
              legitimate circumstances — such as pricing errors, stock shortages, or suspected fraud —
              and will notify you and issue a refund where payment has already been taken.
            </InfoCard>
          </PolicySection>

          <PolicySection id="products" number="4" title="Product Information">
            <p>We aim for product listings to be accurate, but please note:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Product images may vary slightly from the item you receive due to lighting, photography, or manufacturing variance.</li>
              <li>Colors can appear differently depending on your device's display settings.</li>
              <li>Where measurements are provided, please check them against your own before ordering.</li>
              <li>We correct listing errors when we find them and never intend product descriptions to mislead.</li>
            </ul>
          </PolicySection>

          <PolicySection id="shipping-delivery" number="5" title="Shipping & Delivery">
            <p>
              Shipping charges, delivery timelines, and carrier handling are covered in detail in our{" "}
              <a href="/shipping-policy" className="text-rose-500 underline hover:text-rose-600">Shipping Policy</a>,
              which forms part of these Terms. In short: shipping charges are shown before you pay, and
              delivery timelines are estimates that can be affected by factors outside our control.
            </p>
          </PolicySection>

          <PolicySection id="cancel-return-refund" number="6" title="Order Cancellation, Returns, Refunds & Exchanges">
            <p>These are related but distinct processes:</p>
            <div className="grid sm:grid-cols-2 gap-3 my-4">
              <InfoCard icon="✋" title="Cancellation">
                Stopping an order before it ships. Cancellation rules: <Placeholder value={SHIPPING.cancellationRule} />.
              </InfoCard>
              <InfoCard icon="↩️" title="Return">
                Sending back an item you've already received, within <Placeholder value={SHIPPING.returnWindow} /> of delivery, subject to our return conditions.
              </InfoCard>
              <InfoCard icon="💰" title="Refund">
                Money returned to your original payment method once a return or cancellation is approved, typically within <Placeholder value={SHIPPING.refundProcessingTime} />.
              </InfoCard>
              <InfoCard icon="🔁" title="Exchange">
                Replacing an item for a different size, color, or product, where available, instead of a refund.
              </InfoCard>
            </div>
            <p>Separately:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Damaged item:</strong> Contact support with photos as soon as possible; we'll arrange a replacement or refund.</li>
              <li><strong>Incorrect item:</strong> Let us know and we'll arrange the correct item or a refund.</li>
              <li><strong>Missing item:</strong> Contact support with your order number so we can investigate.</li>
            </ul>
          </PolicySection>

          <PolicySection id="promotions" number="7" title="Promotions, Discounts & Gift Cards">
            <p>
              Promotional codes and discounts are subject to their own stated terms (such as minimum
              order value, expiry date, or usage limits) and may be withdrawn or modified at any time
              before use. Where gift cards are offered, they are subject to any terms displayed at the
              time of purchase or redemption.
            </p>
          </PolicySection>

          <PolicySection id="reviews" number="8" title="Reviews & User-Generated Content">
            <p>If you submit a review or other content to our site, you agree that it will:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Reflect your genuine experience — no fake or incentivized reviews without disclosure.</li>
              <li>Not be illegal, abusive, defamatory, discriminatory, or fraudulent.</li>
              <li>Not impersonate another person or misrepresent your identity or affiliation.</li>
            </ul>
            <p>
              We may moderate, edit, or remove content that violates this policy. By submitting
              content, you grant us a license to display and use it in connection with operating and
              promoting the site, consistent with applicable law.
            </p>
          </PolicySection>

          <PolicySection id="ip" number="9" title="Intellectual Property & Website Content">
            <p>
              All content on this site — including text, graphics, logos, and design — is owned by or
              licensed to {BUSINESS.legalName} and is protected by applicable intellectual property
              laws. You may not copy, reproduce, or use this content for commercial purposes without
              our permission.
            </p>
          </PolicySection>

          <PolicySection id="conduct" number="10" title="Prohibited Activities & Fraud Prevention">
            <p>When using this site, you agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Attempt to gain unauthorized access to our systems or another customer's account.</li>
              <li>Use the site for any unlawful purpose or to commit fraud.</li>
              <li>Interfere with the site's normal operation or security.</li>
              <li>Submit false payment, shipping, or account information.</li>
            </ul>
            <p>
              We may use reasonable measures to detect suspicious transactions, prevent fraud, and
              protect both customers and the business, including verifying orders where necessary.
            </p>
          </PolicySection>

          <PolicySection id="third-party" number="11" title="Third-Party Services & External Links">
            <p>
              Our site may link to or rely on third-party services — for example, our payment
              processor ({PROVIDERS.paymentProcessor}) and shipping partners ({" "}
              <Placeholder value={PROVIDERS.shippingPartners} />). We aren't responsible for the
              content, policies, or practices of third-party sites or services we don't control.
            </p>
          </PolicySection>

          <PolicySection id="liability" number="12" title="Errors, Limitation of Liability, Force Majeure & Disputes">
            <p>
              We work to keep this site accurate and available, but errors can occur — including
              pricing, listing, or availability errors — and we may correct them, including by
              cancelling affected orders with a full refund.
            </p>
            <p>
              To the extent permitted by applicable law, {BUSINESS.legalName} is not liable for
              indirect, incidental, or consequential damages arising from your use of the site or
              purchase of products, beyond amounts you've actually paid us for the relevant order.
              Some limitations may not apply where prohibited by applicable law.
            </p>
            <p>
              We are not responsible for delays or failures caused by events beyond our reasonable
              control ("force majeure"), such as natural disasters, extreme weather, carrier
              disruptions, or government action.
            </p>
            <p>
              If a dispute arises, we encourage you to contact support first so we can try to resolve
              it directly. These Terms are governed by the laws of{" "}
              <Placeholder value={BUSINESS.governingState} />, without regard to conflict-of-law
              principles, except where applicable consumer protection law provides otherwise.
            </p>
          </PolicySection>

          <PolicySection id="general" number="13" title="General Terms">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Changes to these Terms:</strong> We may update these Terms from time to time. The "Last updated" date at the top reflects the most recent version, and continued use of the site after changes means you accept the updated Terms.</li>
              <li><strong>Severability:</strong> If any part of these Terms is found unenforceable, the rest remain in full effect.</li>
              <li><strong>Entire agreement:</strong> These Terms, together with our Shipping and Privacy Policies, make up the full agreement between you and {BUSINESS.legalName} regarding use of this site.</li>
              <li><strong>Indemnification:</strong> Where permitted by applicable law, you agree to hold us harmless from claims arising out of your misuse of the site or violation of these Terms.</li>
            </ul>
          </PolicySection>
        </div>
      </div>

      <PolicyFAQ
        title="Terms FAQ"
        items={[
          { q: "Can my order be cancelled after I've paid?", a: "In limited, legitimate circumstances — such as pricing errors, stock shortages, or suspected fraud — yes, and we'll always notify you and refund your payment if that happens." },
          { q: "What if the product I received doesn't match the photos?", a: "Minor variations in color or lighting can occur, but if an item is materially different from its listing, contact support and we'll make it right." },
          { q: "How long do I have to return an item?", a: <>Our current return window is <Placeholder value={SHIPPING.returnWindow} />. See our Returns & Refunds page for full details.</> },
          { q: "Do you check orders for fraud?", a: "Yes, we may run reasonable checks on orders to protect both customers and the business from fraudulent transactions." },
          { q: "Can I leave a product review?", a: "Yes — we welcome genuine reviews. Reviews that are fake, abusive, or violate our content guidelines may be removed." },
          { q: "What law governs these Terms?", a: <>These Terms are governed by the laws of <Placeholder value={BUSINESS.governingState} />, subject to applicable consumer protection law in your location.</> },
        ]}
      />

      <TrustStrip />
      <PolicyContact />
      <RelatedPolicies exclude="Terms & Conditions" />
    </PolicyLayout>
  );
}